import React, { useEffect, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { fetchTrends, deleteTrend, updateTrendOrders, searchTrends, bulkDeleteTrends, bulkUpdateTrendStatus } from "./trendSlice";
import Swal from 'sweetalert2';
import ConfirmationModal from "../../components/ConfirmationModal";
import Loader from "../../components/Loader";
import { useNavigate } from "react-router-dom";
import toast from "react-hot-toast";
import log from "../../logger";
import { paths } from "../../routes/helpers";
import { getImageUrl } from "../../utils/urlUtils";
import DndTable from "../../components/DndTable";
import LoadingSpinner from "../../components/LoadingSpinner";
import useDebounce from "../../hooks/useDebounce";

const TrendList = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { items, loading, error, total } = useSelector((state) => state.trend);

  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [selectedTrend, setSelectedTrend] = useState(null);
  const [displayLimit, setDisplayLimit] = useState(10);
  const [hasMore, setHasMore] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const debouncedSearchTerm = useDebounce(searchTerm, 1000);

  useEffect(() => {
    if (debouncedSearchTerm) {
      dispatch(searchTrends({ keyword: debouncedSearchTerm, page: 1, limit: displayLimit }));
    } else {
      dispatch(fetchTrends({ page: 1, limit: displayLimit }));
    }
  }, [dispatch, displayLimit, debouncedSearchTerm]);

  const handleDelete = (trendId) => {
    setShowDeleteModal(true);
    setSelectedTrend(trendId);
  };

  const confirmDelete = async () => {
    if (selectedTrend) {
      try {
        await dispatch(deleteTrend(selectedTrend)).unwrap();
        toast.success("Trend deleted successfully!");
        log.info("Trend deleted:", selectedTrend);
      } catch (err) {
        toast.error(err || "Failed to delete trend");
        log.error("Error deleting trend:", err);
      } finally {
        setShowDeleteModal(false);
        setSelectedTrend(null);
      }
    }
  };

  const handleEdit = (trendId) => {
    navigate(paths.private.trend.edit(trendId));
  };

  const handleCreateTrend = () => {
    navigate(paths.private.trend.create());
  };

  const handleLoadMore = () => {
    const newLimit = displayLimit + 10;
    setDisplayLimit(newLimit);
    
    // Check if we've loaded all items
    if (newLimit >= total) {
      setHasMore(false);
    }
  };

  const getRemainingItemsCount = () => {
    return total - Math.min(displayLimit, items.length);
  };

  const handleDragEnd = async (event) => {
    const { active, over } = event;
  
    // Step 1: Check if dragged item ka position change hua ha
    if (active.id !== over.id) {
      const oldIndex = items.findIndex(item => item._id === active.id);
      const newIndex = items.findIndex(item => item._id === over.id);
  
      // Step 2: Purani list ki ek copy banao (kyunke Redux state direct mutate ni karni chahiye)
      const reorderedItems = [...items];
  
      // Step 3: Dragged item ko nikaal ke usay new index pr daalo
      const [movedItem] = reorderedItems.splice(oldIndex, 1);
      reorderedItems.splice(newIndex, 0, movedItem);
  
      // Step 4: Har item ko naya order assign karo (1 se start krke)
      const updatedTrends = reorderedItems.map((item, index) => ({
        id: item._id,
        order: index + 1 // 🟢 1 se start krna best ha (optional)
      }));
  
      try {
        // Step 5: UI me Redux state ko update karo
        dispatch({ type: 'trend/reorderTrends', payload: { oldIndex, newIndex } });
  
        // Step 6: Backend ko naya order bhejo
        await dispatch(updateTrendOrders(updatedTrends)).unwrap();
  
        toast.success('Trend order updated successfully!');
      } catch (error) {
        // Step 7: Agar kuch ghalat ho jaye to dobara se data fetch kro
        dispatch(fetchTrends({ page: 1, limit: displayLimit }));
        toast.error('Failed to update trend order');
        log.error('Error updating trend order:', error);
      }
    }
  };

  const handleSearch = (searchValue) => {
    setSearchTerm(searchValue);
    setDisplayLimit(10); // Reset to initial limit when searching
    setHasMore(true);
  };

  const handleBulkDelete = async (ids, clearSelection) => {
    const result = await Swal.fire({
      title: `Delete ${ids.length} trend${ids.length > 1 ? 's' : ''}?`,
      text: 'This action cannot be undone!',
      icon: 'warning',
      showCancelButton: true,
      confirmButtonColor: '#d33',
      cancelButtonColor: '#3085d6',
      confirmButtonText: 'Yes, delete',
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    try {
      await dispatch(bulkDeleteTrends(ids)).unwrap();
      Swal.fire({ title: 'Deleted!', text: `${ids.length} trend${ids.length > 1 ? 's' : ''} deleted.`, icon: 'success', timer: 1500, showConfirmButton: false });
    } catch (err) {
      Swal.fire({ title: 'Some deletions failed', text: err || 'One or more items are still in use elsewhere and were not deleted.', icon: 'error' });
    } finally {
      clearSelection();
      if (debouncedSearchTerm) {
        dispatch(searchTrends({ keyword: debouncedSearchTerm, page: 1, limit: displayLimit }));
      } else {
        dispatch(fetchTrends({ page: 1, limit: displayLimit }));
      }
    }
  };

  const handleBulkStatusChange = (status) => async (ids, clearSelection) => {
    const activating = status === 1;
    const result = await Swal.fire({
      title: `${activating ? 'Activate' : 'Deactivate'} ${ids.length} trend${ids.length > 1 ? 's' : ''}?`,
      icon: 'question',
      showCancelButton: true,
      confirmButtonColor: '#3085d6',
      cancelButtonColor: '#d33',
      confirmButtonText: `Yes, ${activating ? 'activate' : 'deactivate'}`,
      cancelButtonText: 'Cancel',
    });
    if (!result.isConfirmed) return;

    dispatch(bulkUpdateTrendStatus({ ids, status })).then(() => {
      Swal.fire({ title: 'Updated!', text: `${ids.length} trend${ids.length > 1 ? 's' : ''} ${activating ? 'activated' : 'deactivated'}.`, icon: 'success', timer: 1500, showConfirmButton: false });
      clearSelection();
    });
  };

  const columns = [
    {
      key: 'image',
      label: 'Image',
      render: (value) => value && (
        <img src={value} alt="Trend" className="w-12 h-12 object-cover rounded" />
      )
    },
    { key: 'name', label: 'Name' },
    { key: 'categories', label: 'Categories' },
    { key: 'status', label: 'Status' },
    { key: 'createdAt', label: 'Created At' },
    { key: 'updatedAt', label: 'Updated At' },
  ];

  const actions = [
    { label: 'Edit', onClick: handleEdit, color: 'blue' },
    { label: 'Delete', onClick: handleDelete, color: 'red' },
  ];

  const formatTableData = (trends) => {
    return trends.map((trend) => ({
      _id: trend._id,
      name: trend.label || '-',
      categories: trend.categoryIds ? 
        (Array.isArray(trend.categoryIds) ? 
          trend.categoryIds.map(cat => cat.name || cat.label || '-').join(', ') : 
          (trend.categoryIds.name || trend.categoryIds.label || '-')
        ) : '-',
      image: getImageUrl(trend.image) || null,
      status: trend.status ? 'Active' : 'Inactive',
      createdAt: trend.createdAt ? new Date(trend.createdAt).toLocaleString() : '-',
      updatedAt: trend.updatedAt ? new Date(trend.updatedAt).toLocaleString() : '-',
    }));
  };

  if (loading && items.length === 0) return (
    <div className="p-4">
      <Loader />
    </div>
  );

  if (error) return (
    <div className="p-4">
      <div className="text-red-500">Error: {error}</div>
    </div>
  );

  return (
    <div className="p-4">
      <h1 className="text-xl font-bold mb-4">Trends</h1>
      <div className="flex justify-between items-center mb-4">
        <button
          onClick={handleCreateTrend}
          className="bg-green-500 text-white px-4 py-2 rounded"
        >
          Create New Trend
        </button>
        <div className="text-sm text-gray-600">
          {searchTerm ? `Found ${total} trends` : `Showing ${Math.min(displayLimit, items.length)} of ${total} trends`}
        </div>
      </div>

      {/* Always show the table for search functionality */}
      <DndTable
        items={formatTableData(items)}
        columns={columns}
        actions={actions}
        onDragEnd={handleDragEnd}
        onSearch={handleSearch}
        searchDelay={1000}
        searchValue={searchTerm}
        enableRowSelection={true}
        bulkActions={[
          { label: 'Activate Selected', color: 'green', onClick: handleBulkStatusChange(1) },
          { label: 'Deactivate Selected', color: 'gray', onClick: handleBulkStatusChange(0) },
          { label: 'Delete Selected', color: 'red', onClick: handleBulkDelete },
        ]}
      />

      {/* Show load more button only if there are items */}
      {items.length > 0 && hasMore && items.length < total && (
        <div className="mt-6 flex flex-col items-center space-y-2">
          <button
            onClick={handleLoadMore}
            disabled={loading}
            className={`
              inline-flex items-center justify-center
              px-6 py-3 rounded-lg
              text-base font-medium
              transition-all duration-200 ease-in-out
              ${loading 
                ? "bg-gray-100 text-gray-500 cursor-not-allowed"
                : "bg-blue-500 hover:bg-blue-600 text-white hover:shadow-lg transform hover:-translate-y-0.5"
              }
            `}
          >
            {loading ? (
              <>
                <LoadingSpinner className="mr-2" />
                <span>Loading...</span>
              </>
            ) : (
              <>
                <svg 
                  className="w-5 h-5 mr-2" 
                  fill="none" 
                  stroke="currentColor" 
                  viewBox="0 0 24 24"
                >
                  <path 
                    strokeLinecap="round" 
                    strokeLinejoin="round" 
                    strokeWidth={2} 
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
                <span>Load More</span>
              </>
            )}
          </button>
          <p className="text-sm text-gray-500">
            {getRemainingItemsCount()} more items to load
          </p>
        </div>
      )}

      {/* Show all loaded message only if there are items */}
      {items.length > 0 && !hasMore && (
        <div className="mt-6 text-center">
          <p className="text-sm text-gray-500">
            All trends loaded
          </p>
        </div>
      )}

      {/* Show no results message only if there are no items */}
      {items.length === 0 && (
        <div className="text-center py-8 bg-gray-50 rounded-lg mt-4">
          <p className="text-gray-500">
            {searchTerm ? `No trends found for "${searchTerm}"` : 'No trends found'}
          </p>
          <p className="text-gray-400 text-sm mt-2">
            {searchTerm ? 'Try a different search term' : 'Click the Create New button to add one'}
          </p>
        </div>
      )}

      {showDeleteModal && (
        <ConfirmationModal
          message="Are you sure you want to delete this trend?"
          onConfirm={confirmDelete}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};

export default TrendList;